import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import isEmpty from 'lodash.isempty';
import { Form, Formik } from 'formik';
import styled from 'styled-components';
import SectionLoader from '../../Loader/SectionLoader';
import ModalButtons from '../../Modal/ModalButtons';
import {
  fetchCreditCards,
  submitCreditCard,
} from '../../../store/actions/creditCard/creditCardActions';
import { uFlexCenter } from '../../../assets/styles/utility';
import { pxToRem } from '../../../assets/styles/helper';
import themeColors from '../../../assets/styles/themeColors';
import { variables } from '../../../assets/styles/variables';
import {
  getCurrentAccountUid,
  selectAccountAddresses,
} from '../../../store/selectors/accountSelectors';
import {
  addCreditCardExistingAddressValidationSchema,
  addCreditCardNewAddressValidationSchema,
} from '../../../validation/addCreditCardValidationSchema';
import { fetchAccountAddresses } from '../../../store/actions/account/accountActions';
import { selectIsLoadingByActionTypes } from '../../../store/selectors/loadingSelectors';
import { SUBMIT_CREDIT_CARD_LOADING } from '../../../store/actions/creditCard/creditCardActionConstants';
import { FETCH_ACCOUNT_ADDRESSES_LOADING } from '../../../store/actions/account/accountActionConstants';
import {
  GET_COUNTRIES_LOADING,
  GET_STATES_LOADING,
} from '../../../store/actions/common/commonConstants';
import {
  // formatAddressStringInline,
  getStatesOptions,
} from '../../../util/helpers/addressHelper';
import { USA_COUNTRY_ISO3CODE } from '../../../util/constants';
import { selectStates } from '../../../store/selectors/commonSelectors';
import DeliveryAddress from '../../Buy/DeliveryAddress';
import { DeliveryAddressWrapper } from '../../Buy/SelectAddress';
import AddDeliveryAddressModal from '../Address/DeliveryAddressModal';
import { removeFromSessionStorage } from '../../../util/helpers/sessionStorageHelper';
import { ADD_ADDRESS_WIZARD } from '../../../constants/sessionStorage';

const StepWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${pxToRem(12)};
`;

const StepNumberNote = styled.p`
  font-size: 14px;
  font-weight: 500;
  color: ${themeColors.colorTextPrimary};
`;

const StepTitle = styled.p`
  font-size: 14px;
  font-weight: 400;
  color: ${themeColors.colorTextSecondary};
`;

export const AddAddressPlusIconWrapper = styled.div`
  ${uFlexCenter};
  width: ${pxToRem(16)};
  height: ${pxToRem(16)};
  border-radius: 4px;
  background-color: ${themeColors.colorPrimary};
  box-shadow: ${variables.shadow.boxShadow};
  margin-right: ${pxToRem(16)};
  flex-shrink: 0;
  padding: ${pxToRem(3)};
  color: ${themeColors.colorTextContrast};

  svg {
    width: ${pxToRem(16)};
    height: ${pxToRem(16)};
    color: ${themeColors.colorTextContrast};
  }
`;

const AddNewAddressButton = styled.button`
  width: 100%;
  border-radius: ${variables.borderRadius.borderRadius};
  border: 1px dashed ${themeColors.colorGrey3};
  font-size: ${pxToRem(14)};
  line-height: 1.4;
  font-weight: 500;
  color: ${themeColors.colorTextPrimary};
  background-color: transparent;
  padding: ${pxToRem(10)};
  cursor: pointer;
`;

const ButtonsWrapper = styled.div`
  flex-shrink: 0;
  margin-top: auto;
  background-color: ${themeColors.colorWhite};
  padding-top: ${pxToRem(12)};
  padding-bottom: ${pxToRem(8)};
  position: sticky;
  bottom: 0;
  z-index: 1;
`;

const ModalContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const ScrollableAddressList = styled.div`
  flex: 1;
  overflow-y: auto;
  margin-top: ${pxToRem(8)};
  margin-bottom: 0;
  padding-right: ${pxToRem(4)};
  padding-bottom: ${pxToRem(12)};
  scrollbar-width: thin;
  scrollbar-color: ${themeColors.colorGrey3} transparent;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: ${themeColors.colorGrey3};
    border-radius: 4px;
  }
`;

const EnterBillingAddressStep = ({
  handleBack,
  wizardData,
  steps,
  postPaidFlow,
  setIsNewCardAdded,
  onButtonClick,
  setIsNewACHAdded,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const accountUid = useSelector(getCurrentAccountUid);
  const accountAddresses = useSelector(selectAccountAddresses);
  const [isLoading, setIsLoading] = useState(false);
  const [isAddAddressOpen, setIsAddAddressOpen] = useState(false);

  const isDataLoading = useSelector(
    selectIsLoadingByActionTypes([
      SUBMIT_CREDIT_CARD_LOADING,
      GET_STATES_LOADING,
      GET_COUNTRIES_LOADING,
      FETCH_ACCOUNT_ADDRESSES_LOADING,
    ]),
  );

  const states = useSelector(selectStates);

  const STATES_OPTIONS = getStatesOptions(states);

  useEffect(() => {
    if (accountUid) {
      dispatch(fetchAccountAddresses({ accountUid }));
    }
  }, [dispatch, accountUid]);

  const onSuccess = () => {
    dispatch(fetchCreditCards({ accountUid }));
    if (postPaidFlow) {
      setIsNewCardAdded(true);
      setIsNewACHAdded(false);
    }
    onButtonClick();
  };

  const handleSubmitCreditCard = (values) => {
    const addressPayload =
      !isAddAddressOpen && !isEmpty(accountAddresses)
        ? { AddressUid: values.AddressUid }
        : {
            BillingAddress: {
              AddressLine: values.AddressLine,
              AddressLine2: values.AddressLine2,
              City: values.City,
              PostalCode: values.PostalCode,
              StateRegion: values.StateRegion,
              CountryIso3Code: values.CountryIso3Code,
            },
          };
    dispatch(
      submitCreditCard({
        AccountUid: accountUid,
        data: postPaidFlow
          ? {
              ...wizardData.creditCard,
              ...addressPayload,
            }
          : {
              ...wizardData,
              ...addressPayload,
            },
        setIsLoading,
        onSuccess,
        handleNext: onButtonClick,
      }),
    );
  };

  const defaultAddress = accountAddresses?.find(
    (address) => address.IsDefaultMailingAddress,
  );

  if (isAddAddressOpen) {
    return (
      <AddDeliveryAddressModal
        isOpen={isAddAddressOpen}
        onClose={() => {
          removeFromSessionStorage(ADD_ADDRESS_WIZARD);
          setIsAddAddressOpen(false);
        }}
        title={t('buyWizard.addYourBillingAddress')}
        page="buy"
        avoidClearingStorage
        isAddingCCFlow
      />
    );
  }

  return (
    <SectionLoader isLoading={isLoading && isDataLoading}>
      {/* <ModalBody css={uLayoutStyle}> */}
      <StepWrapper>
        <StepNumberNote>
          {t('creditCard.stepNumber', {
            currentStepNumber: 2,
            numberOfSteps: steps.length,
          })}
        </StepNumberNote>
        <StepTitle>{t('creditCard.billingAddressStep')}</StepTitle>
      </StepWrapper>

      <Formik
        initialValues={{
          // AddressUid: {
          //   value: defaultAddress
          //     ? defaultAddress.AddressUid
          //     : accountAddresses[0]?.AddressUid,
          //   label: formatAddressStringInline(
          //     defaultAddress ?? accountAddresses[0],
          //     countries,
          //   ),
          // },
          AddressUid: defaultAddress
            ? defaultAddress.AddressUid
            : accountAddresses[0]?.AddressUid || '',
          CountryIso3Code: USA_COUNTRY_ISO3CODE,
          AddressLine: '',
          AddressLine2: '',
          City: '',
          StateRegion: STATES_OPTIONS[0]?.value || '',
          PostalCode: '',
          TagType: '',
          TagNote: '',
        }}
        onSubmit={handleSubmitCreditCard}
        validationSchema={
          isAddAddressOpen || isEmpty(accountAddresses)
            ? addCreditCardNewAddressValidationSchema
            : addCreditCardExistingAddressValidationSchema
        }
        enableReinitialize
      >
        {({ isValid, values, setFieldValue }) => (
          <Form>
            <ModalContentWrapper>
              <ScrollableAddressList>
                <DeliveryAddressWrapper>
                  <AddNewAddressButton
                    onClick={() => {
                      setIsAddAddressOpen(true);
                    }}
                  >
                    {t('buyWizard.addNewAddress')}
                  </AddNewAddressButton>
                  {!isEmpty(accountAddresses) && (
                    <>
                      {accountAddresses
                        .sort((a, b) => {
                          if (
                            a.IsDefaultMailingAddress &&
                            !b.IsDefaultMailingAddress
                          )
                            return -1;
                          if (
                            !a.IsDefaultMailingAddress &&
                            b.IsDefaultMailingAddress
                          )
                            return 1;
                          return 0;
                        })
                        .map((address) => (
                          <DeliveryAddress
                            key={address.AddressUid}
                            address={address}
                            isSelected={
                              values.AddressUid === address.AddressUid
                            }
                            onClick={() =>
                              setFieldValue('AddressUid', address.AddressUid)
                            }
                            isAddingCCFlow
                          />
                        ))}
                    </>
                  )}
                </DeliveryAddressWrapper>

                {/* {(isEmpty(accountAddresses) || isAddingNewAddress) && (
              <Add
                stateOptions={STATES_OPTIONS}
                setFieldValue={setFieldValue}
                {...values}
                values={values}
              />
            )} */}
              </ScrollableAddressList>
              <ButtonsWrapper>
                <ModalButtons
                  isHorizontal
                  marginTop
                  primaryButtonProps={{
                    type: 'submit',
                    label: t('common.submit'),
                    disabled: !isValid,
                  }}
                  secondaryButtonProps={{
                    onClick: handleBack,
                    label: t('common.back'),
                  }}
                />
              </ButtonsWrapper>
            </ModalContentWrapper>
          </Form>
        )}
      </Formik>
      {/* </ModalBody> */}
    </SectionLoader>
  );
};

EnterBillingAddressStep.propTypes = {
  handleBack: PropTypes.func,
  wizardData: PropTypes.shape({
    Bin: PropTypes.number,
    Last4Digits: PropTypes.number,
    ExpirationDate: PropTypes.string,
    NameOnCard: PropTypes.string,
    Nonce: PropTypes.string,
    CreditCardType: PropTypes.string,
    creditCard: PropTypes.shape({
      Last4Digits: PropTypes.string,
      ExpirationDate: PropTypes.string,
    }),
  }),
  steps: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      percentage: PropTypes.number,
    }),
  ),
  // paymentMethods: PropTypes.shape(),
  // setPaymentMethods: PropTypes.func,
  // setPaymentMethod: PropTypes.func,
  postPaidFlow: PropTypes.bool,
  setIsNewCardAdded: PropTypes.func,
  setIsNewACHAdded: PropTypes.func,
  onButtonClick: PropTypes.func,
};

export default EnterBillingAddressStep;
